import React, { useState } from 'react';
import { BuroCombobox } from './BuroCombobox';
import { BuroPortal, fechaCorte, mensajeError, mesLargo, mesNombre, mesesTerminados, money0 } from './helpers';

/*
 * Modales del reporte (P2, P5, P6, P8 del diseño) y el loader de operaciones
 * largas. Ninguno llama a la API: avisan con onConfirm/onCrear y quien los
 * abre decide qué hacer y cómo mostrar el progreso.
 */

const Marco = ({ onClose, children }) => (
  <BuroPortal>
    <div className="bu-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bu-modal" role="dialog" aria-modal="true">{children}</div>
    </div>
  </BuroPortal>
);

export const Cargando = ({ texto }) => (
  <BuroPortal>
    <div className="bu-loading"><div><i className="pi pi-spin pi-spinner" />{texto}</div></div>
  </BuroPortal>
);

const TIPOS_REPORTE = [
  { value: 'pf', label: 'Personas Físicas' },
  { value: 'pm_pfae', label: 'Personas Morales y PFAE' },
];

// P2 · Crear reporte. `reportes` sirve para avisar si el mes ya existe.
export const CrearReporteModal = ({ reportes, onClose, onCrear, onAbrir }) => {
  const meses = mesesTerminados(12);
  const [tipo, setTipo] = useState('pf');
  const [mes, setMes] = useState(meses[0]);
  const existente = reportes.find((r) => String(r.tipo).toLowerCase() === tipo && String(r.periodo).slice(0, 7) === mes);
  const corte = fechaCorte(mes);

  return (
    <Marco onClose={onClose}>
      <h3>Crear reporte</h3>
      <div className="bu-grid-m">
        <div className="bu-label">Tipo</div>
        <BuroCombobox ariaLabel="Tipo de reporte" placeholder="Elige el tipo" options={TIPOS_REPORTE} value={tipo} onChange={setTipo} />
        <div className="bu-label">Mes</div>
        <BuroCombobox ariaLabel="Mes" placeholder="Elige el mes" options={meses.map((m) => ({ value: m, label: mesLargo(m) }))} value={mes} onChange={setMes} />
        <div className="bu-label">Fecha de reporte</div>
        <div className="tnum">{corte}</div>
      </div>
      <div className="bu-note bu-note-info">
        <i className="pi pi-info-circle" />
        <span>Se tomarán los pagos con fecha de depósito hasta el {corte}.</span>
      </div>
      {existente && (
        <div className="bu-note bu-note-warn" style={{ alignItems: 'center' }}>
          <i className="pi pi-exclamation-circle" style={{ marginTop: 0 }} />
          <span style={{ flex: 1 }}>Ya existe el reporte de {mesNombre(mes)}</span>
          <button type="button" className="bu-btn bu-btn-sug" style={{ height: 30 }} onClick={() => onAbrir(existente)}>Abrir</button>
        </div>
      )}
      <div className="bu-actions">
        <button type="button" className="bu-btn" onClick={onClose}>Cancelar</button>
        <button type="button" className="bu-btn bu-btn-primary" disabled={!!existente} onClick={() => onCrear(tipo, mes)}>Crear</button>
      </div>
    </Marco>
  );
};

const MOTIVOS_EXCLUSION = ['Es persona moral / PFAE', 'Cerrado antes del periodo', 'Otro'];

// P5 · Excluir contrato. Motivo + nota viajan juntos en un solo texto.
export const ExcluirModal = ({ tipo = 'pf', fila, onClose, onConfirm }) => {
  const motivos = tipo === 'pm_pfae' ? ['Es persona física', ...MOTIVOS_EXCLUSION.slice(1)] : MOTIVOS_EXCLUSION;
  const [motivo, setMotivo] = useState(motivos[0]);
  const [nota, setNota] = useState('');
  return (
    <Marco onClose={onClose}>
      <h3>Excluir {fila.numero_cuenta} del reporte</h3>
      <div className="bu-sub-h">{fila.cliente}</div>
      <div className="bu-field">
        <div className="bu-label">Motivo *</div>
        <BuroCombobox ariaLabel="Motivo" placeholder="Elige el motivo" options={motivos.map((m) => ({ value: m, label: m }))} value={motivo} onChange={setMotivo} />
      </div>
      <div className="bu-field">
        <div className="bu-label">Nota</div>
        <input className="bu-input" value={nota} onChange={(e) => setNota(e.target.value)} />
      </div>
      <div className="bu-actions">
        <button type="button" className="bu-btn" onClick={onClose}>Cancelar</button>
        <button type="button" className="bu-btn bu-btn-danger" onClick={() => onConfirm(nota.trim() ? `${motivo} · ${nota.trim()}` : motivo)}>Excluir</button>
      </div>
    </Marco>
  );
};

// P6 · Ajustes del reporte.
export const AjustesModal = ({ ajustes, onClose, onGuardar }) => {
  const [mora, setMora] = useState(!!ajustes.incluir_mora_en_saldo);
  return (
    <Marco onClose={onClose}>
      <h3>Ajustes</h3>
      <label className="bu-check">
        <input type="checkbox" checked={mora} onChange={(e) => setMora(e.target.checked)} />
        <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontWeight: 500 }}>Incluir mora en el saldo actual</span>
          <span className="bu-sub" style={{ fontSize: 13, lineHeight: 1.45 }}>
            La multa por pagar tarde, calculada al día del corte, cuenta como parte de lo que debe el cliente.
          </span>
        </span>
      </label>
      <div className="bu-actions">
        <button type="button" className="bu-btn" onClick={onClose}>Cancelar</button>
        <button type="button" className="bu-btn bu-btn-primary" onClick={() => onGuardar({ incluir_mora_en_saldo: mora })}>Guardar y regenerar</button>
      </div>
    </Marco>
  );
};

export const RegenerarModal = ({ tipo = 'pf', manuales, onClose, onConfirm }) => (
  <Marco onClose={onClose}>
    <h3>Regenerar reporte</h3>
    <div className="bu-sub" style={{ lineHeight: 1.5 }}>
      Se recalculan saldos{tipo === 'pf' ? ', MOP' : ''} y fechas con los pagos actuales de Cartera. Las {manuales} celdas editadas a mano se conservan.
    </div>
    <div className="bu-actions">
      <button type="button" className="bu-btn" onClick={onClose}>Cancelar</button>
      <button type="button" className="bu-btn bu-btn-primary" onClick={onConfirm}>Regenerar</button>
    </div>
  </Marco>
);

// P8 · Cerrar mes. La casilla de contabilidad es solo confirmación de pantalla.
export const CerrarModal = ({ reporte, onClose, onConfirm }) => {
  const [ok, setOk] = useState(false);
  const t = reporte.totales;
  return (
    <Marco onClose={onClose}>
      <h3>Cerrar {mesLargo(reporte.periodo).toLowerCase()}</h3>
      <div>
        Se enviarán {t.contratos} contratos
        {t.excluidos ? ` (${t.excluidos} ${t.excluidos === 1 ? 'excluido' : 'excluidos'})` : ''}.
      </div>
      <div className="bu-totales">
        <div className="k">Saldo actual total</div><div className="v">{money0(t.saldo_actual)}</div>
        <div className="k">Saldo vencido total</div><div className="v">{money0(t.saldo_vencido)}</div>
      </div>
      <div className="bu-note bu-note-warn" style={{ lineHeight: 1.5 }}>
        <i className="pi pi-exclamation-triangle" style={{ marginTop: 3 }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontWeight: 600 }}>Al cerrar:</span>
          <span>· El reporte ya no se podrá editar.</span>
          <span>· Se guarda la fecha de primer incumplimiento de cada contrato.</span>
        </div>
      </div>
      <div className="bu-field" style={{ gap: 8 }}>
        <div className="bu-label">Confirma que contabilidad revisó los saldos</div>
        <label className="bu-radio">
          <input type="checkbox" style={{ accentColor: '#23B56E', width: 18, height: 18 }} checked={ok} onChange={(e) => setOk(e.target.checked)} />
          Sí, los saldos cuadran con contabilidad
        </label>
      </div>
      <div className="bu-actions">
        <button type="button" className="bu-btn" onClick={onClose}>Cancelar</button>
        <button type="button" className="bu-btn bu-btn-primary" disabled={!ok} onClick={onConfirm}>Cerrar mes</button>
      </div>
    </Marco>
  );
};

// Reglas de la contraseña propia del ZIP (las mismas que valida el backend:
// ZipCifradoBuro::passwordValida; el símbolo no puede ser un espacio).
const REGLAS_PASSWORD = [
  ['Al menos 8 caracteres', (p) => [...p].length >= 8],
  ['Una mayúscula', (p) => /\p{Lu}/u.test(p)],
  ['Un número', (p) => /\d/.test(p)],
  ['Un símbolo', (p) => /[^\p{L}\d\s]/u.test(p)],
];

/*
 * Descarga del CSV protegido (ZIP con contraseña). Primero se elige generar
 * una contraseña automática o usar la propia; `onGenerar(password|'')` pide el
 * ZIP, dispara la descarga y devuelve la contraseña. Después se muestra UNA
 * sola vez: al cerrar el modal no queda guardada en ningún lado.
 */
export const CsvProtegidoModal = ({ onClose, onGenerar, tipo = 'pf' }) => {
  const archivo = tipo === 'pm_pfae' ? 'TXT' : 'CSV';
  const [propia, setPropia] = useState(false);
  const [pass, setPass] = useState('');
  const [clave, setClave] = useState(null); // contraseña ya generada
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [copiada, setCopiada] = useState(false);

  const cumple = REGLAS_PASSWORD.map(([, f]) => f(pass));
  const valida = !propia || cumple.every(Boolean);

  const generar = async () => {
    if (cargando) return;
    setCargando(true);
    setError(null);
    try {
      setClave(await onGenerar(propia ? pass : ''));
    } catch (e) {
      setError(mensajeError(e, 'No se pudo generar el ZIP.'));
    } finally {
      setCargando(false);
    }
  };

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(clave);
      setCopiada(true);
    } catch (e) {
      setCopiada(false);
    }
  };

  if (clave) {
    return (
      <Marco onClose={onClose}>
        <h3>Contraseña del ZIP</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <input className="bu-input" readOnly value={clave} style={{ fontFamily: 'ui-monospace, Menlo, monospace' }} onFocus={(e) => e.target.select()} />
          <button type="button" className="bu-btn" onClick={copiar}><i className="pi pi-copy" style={{ fontSize: 13 }} />{copiada ? 'Copiada' : 'Copiar'}</button>
        </div>
        <div className="bu-note bu-note-warn">
          <i className="pi pi-exclamation-triangle" />
          <span>Se muestra una sola vez. Guárdala ahora y envíala por separado. Si la pierdes, descarga de nuevo: se genera otra.</span>
        </div>
        <div className="bu-actions">
          <button type="button" className="bu-btn bu-btn-primary" onClick={onClose}>Listo</button>
        </div>
      </Marco>
    );
  }

  return (
    <Marco onClose={onClose}>
      <h3>Descargar {archivo} protegido</h3>
      <div className="bu-sub" style={{ lineHeight: 1.5 }}>Se descarga un ZIP {tipo === 'pm_pfae' ? 'cifrado con AES-256' : 'cifrado'} con el {archivo}. Envía la contraseña por separado.</div>
      {error && <div className="bu-note bu-note-err"><i className="pi pi-exclamation-triangle" /><span>{error}</span></div>}
      <label className="bu-radio">
        <input type="radio" name="modo-zip" checked={!propia} onChange={() => setPropia(false)} />Generar automática
      </label>
      <label className="bu-radio">
        <input type="radio" name="modo-zip" checked={propia} onChange={() => setPropia(true)} />Usar mi propia contraseña
      </label>
      {propia && (
        <div className="bu-field">
          <input className="bu-input" type="text" autoComplete="off" value={pass} onChange={(e) => setPass(e.target.value)} placeholder="Contraseña del ZIP" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12 }}>
            {REGLAS_PASSWORD.map(([texto], i) => (
              <span key={texto} style={{ color: cumple[i] ? '#1B7F4D' : '#64748B' }}>
                <i className={`pi ${cumple[i] ? 'pi-check' : 'pi-circle'}`} style={{ fontSize: 10, marginRight: 6 }} />{texto}
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="bu-sub" style={{ fontSize: 12 }}>La descarga queda registrada (quién y cuándo).</div>
      <div className="bu-actions">
        <button type="button" className="bu-btn" onClick={onClose}>Cancelar</button>
        <button type="button" className="bu-btn bu-btn-primary" disabled={!valida || cargando} onClick={generar}>
          {cargando ? 'Generando…' : 'Descargar ZIP'}
        </button>
      </div>
    </Marco>
  );
};
